import { useCallback, useEffect, useState } from "react";
import { Ban, CircleCheck, Clock, RotateCcw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api";
import { formatBRL, formatDateRangeUTC, formatDeadline } from "@/lib/format";
import { useToast } from "@/components/ui/Toast";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { ScreenSpinner } from "@/components/ui/Spinner";
import type { TournamentStatus } from "@/types";
import { AdminHeader, canManageUsers, errMessage } from "./shared";

type AdminTournament = {
  id: string;
  name: string;
  course: string | null;
  startDate: string;
  endDate: string;
  deadline: string;
  deadlineOverride: boolean;
  status: TournamentStatus;
  minPot: number | null;
  effectiveMinPot: number;
  jackpotIn: number;
  betsEnabled: boolean;
  totalStaked: number;
  winnersCount: number | null;
  winnersPool: number;
  guaranteedWinnersPool: number;
};

const STATUS_LABEL: Record<TournamentStatus, string> = {
  upcoming: "Próximo",
  active: "Em andamento",
  settled: "Liquidado",
};

const STATUS_TONE: Record<
  TournamentStatus,
  "success" | "accent" | "muted"
> = {
  upcoming: "accent",
  active: "success",
  settled: "muted",
};

const STATUS_ORDER: TournamentStatus[] = ["active", "upcoming", "settled"];

export default function AdminTorneiosPage() {
  const { user: viewer } = useAuth();
  const { showToast } = useToast();
  const [tournaments, setTournaments] = useState<AdminTournament[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Editor de prazo aberto (id do torneio) + valor do input datetime-local.
  const [editingDeadlineId, setEditingDeadlineId] = useState<string | null>(
    null,
  );
  const [deadlineInput, setDeadlineInput] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ tournaments: AdminTournament[] }>(
        "/api/admin/tournaments",
      );
      setTournaments(data.tournaments);
      setError(null);
    } catch (err) {
      setError(errMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleBets = async (t: AdminTournament) => {
    if (busyId) return;
    setBusyId(t.id);
    try {
      await apiFetch(`/api/admin/tournaments/${t.id}/bets-enabled`, {
        method: "PATCH",
        body: JSON.stringify({ betsEnabled: !t.betsEnabled }),
      });
      await load();
      showToast({
        type: "success",
        message: t.betsEnabled
          ? "Apostas desabilitadas para o torneio."
          : "Apostas habilitadas para o torneio.",
      });
    } catch (err) {
      showToast({ type: "error", message: errMessage(err) });
    } finally {
      setBusyId(null);
    }
  };

  // Grava override (ISO) ou, com null, volta à regra padrão do servidor
  // (meio-dia do 2º dia do evento no fuso do local).
  const saveDeadline = async (t: AdminTournament, value: string | null) => {
    if (busyId) return;
    if (value !== null && Number.isNaN(new Date(value).getTime())) {
      showToast({ type: "error", message: "Data/hora inválida." });
      return;
    }
    setBusyId(t.id);
    try {
      await apiFetch(`/api/admin/tournaments/${t.id}/deadline`, {
        method: "PATCH",
        body: JSON.stringify({
          deadline: value === null ? null : new Date(value).toISOString(),
        }),
      });
      await load();
      setEditingDeadlineId(null);
      showToast({
        type: "success",
        message:
          value === null
            ? "Prazo restaurado para a regra padrão."
            : "Prazo do torneio atualizado.",
      });
    } catch (err) {
      showToast({ type: "error", message: errMessage(err) });
    } finally {
      setBusyId(null);
    }
  };

  // datetime-local trabalha em horário local do navegador, sem timezone —
  // converte o instante UTC do servidor para o wall-clock local do admin.
  const toLocalInput = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  if (!viewer) return null;
  const canWrite = canManageUsers(viewer.role);

  return (
    <div className="flex flex-col gap-4 px-5 pt-6">
      <AdminHeader
        title="Torneios"
        caption={
          tournaments ? `${tournaments.length} torneios no calendário` : undefined
        }
      />

      {error && (
        <Card className="p-4 text-center text-sm text-[#ffb4ad]">{error}</Card>
      )}
      {!tournaments && !error && <ScreenSpinner />}

      <div className="flex flex-col gap-5 pb-6">
        {STATUS_ORDER.map((status) => {
          const group = (tournaments ?? []).filter(
            (t) => t.status === status,
          );
          if (group.length === 0) return null;
          return (
            <section key={status} className="flex flex-col gap-2">
              <p className="text-[10px] font-semibold tracking-[1.2px] text-muted-foreground">
                {STATUS_LABEL[status].toUpperCase()} ({group.length})
              </p>
              {group.map((t) => (
                <Card key={t.id} className="flex flex-col gap-2 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold">{t.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {t.course ? `${t.course} · ` : ""}
                        {formatDateRangeUTC(t.startDate, t.endDate)}
                      </p>
                    </div>
                    <Chip tone={STATUS_TONE[t.status]}>
                      {STATUS_LABEL[t.status]}
                    </Chip>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Apostado{" "}
                    <span className="font-semibold text-foreground">
                      {formatBRL(t.totalStaked)}
                    </span>{" "}
                    · pote{" "}
                    <span className="font-semibold text-foreground">
                      {formatBRL(t.guaranteedWinnersPool)}
                    </span>
                    {t.jackpotIn > 0 && ` · jackpot ${formatBRL(t.jackpotIn)}`}
                    {t.winnersCount !== null &&
                      ` · ${t.winnersCount} ganhador${t.winnersCount === 1 ? "" : "es"}`}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Prazo: {formatDeadline(t.deadline)}
                    {t.deadlineOverride ? (
                      <span className="ml-1 font-semibold text-[#B45309]">
                        (ajustado pelo admin)
                      </span>
                    ) : (
                      <span className="ml-1">(regra padrão)</span>
                    )}
                  </p>

                  {t.status !== "settled" &&
                    canWrite &&
                    (editingDeadlineId === t.id ? (
                      <div className="flex flex-col gap-2">
                        <input
                          type="datetime-local"
                          value={deadlineInput}
                          onChange={(e) => setDeadlineInput(e.target.value)}
                          className="h-9 rounded-(--radius-field) border border-border bg-card px-3 text-xs text-foreground outline-none"
                        />
                        <div className="flex gap-2">
                          <Button
                            title="Salvar prazo"
                            loading={busyId === t.id}
                            onClick={() => void saveDeadline(t, deadlineInput)}
                            className="h-9 flex-1 text-xs"
                          />
                          <Button
                            title="Cancelar"
                            variant="ghost"
                            disabled={busyId === t.id}
                            onClick={() => setEditingDeadlineId(null)}
                            className="h-9 flex-1 text-xs"
                          />
                        </div>
                        {t.deadlineOverride && (
                          <Button
                            title="Voltar à regra padrão (12h do 2º dia, hora local do evento)"
                            variant="ghost"
                            icon={<RotateCcw size={14} />}
                            loading={busyId === t.id}
                            onClick={() => void saveDeadline(t, null)}
                            className="h-9 text-xs"
                          />
                        )}
                      </div>
                    ) : (
                      <Button
                        title="Ajustar prazo"
                        variant="ghost"
                        icon={<Clock size={14} />}
                        disabled={busyId !== null}
                        onClick={() => {
                          setDeadlineInput(toLocalInput(t.deadline));
                          setEditingDeadlineId(t.id);
                        }}
                        className="h-9 text-xs"
                      />
                    ))}

                  {t.status !== "settled" && canWrite && (
                    <Button
                      title={
                        t.betsEnabled
                          ? "Desabilitar apostas"
                          : "Habilitar apostas"
                      }
                      variant="ghost"
                      icon={
                        t.betsEnabled ? (
                          <Ban size={14} />
                        ) : (
                          <CircleCheck size={14} />
                        )
                      }
                      loading={busyId === t.id}
                      onClick={() => void toggleBets(t)}
                      className={`h-9 text-xs ${
                        t.betsEnabled
                          ? "border-destructive/50 text-[#ffb4ad] hover:bg-destructive/10"
                          : "border-tint/50 text-tint"
                      }`}
                    />
                  )}
                </Card>
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
